/* eslint react-hooks/immutability: ["warn"] */
import { FixaQuantityStepper } from "@coworker/apprestructured/src/shared/wrappers/FixaQuantityStepper/FixaQuantityStepper";
import React, { useEffect, useState } from "react";
import { logEvent } from "@coworker/app/src/helpers/tracker";
import { AggregatedArticle } from "@coworker/app/src/components/RoomSettings/hooks/useAggregatedArticles";
import { useUpdateArticle } from "@coworker/app/src/components/RoomSettings/hooks/useUpdateArticle";
import { useTranslation } from "@coworker/locales";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";

const delayedUpdateTimer: Record<string, NodeJS.Timeout> = {};

interface ItemQuantityStepperProps {
  article: AggregatedArticle | Article;
  itemNo: string;
  itemQuantityPieces: number;
}

export const ItemQuantityStepper = ({
  itemNo,
  itemQuantityPieces,
  article,
}: ItemQuantityStepperProps) => {
  const [quantity, setQuantity] = useState(itemQuantityPieces);
  const [debounce, setDebounce] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      if (delayedUpdateTimer[itemNo]) {
        clearTimeout(delayedUpdateTimer[itemNo]);
        delete delayedUpdateTimer[itemNo];
      }
    };
  }, [itemNo]);

  // Do not update local quantity stepper for some time after input from the quantity stepper.
  if (debounce) {
    if (delayedUpdateTimer[itemNo]) {
      clearTimeout(delayedUpdateTimer[itemNo]);
    }
    delayedUpdateTimer[itemNo] = setTimeout(() => {
      setDebounce(false);
      setQuantity(itemQuantityPieces);
      delete delayedUpdateTimer[itemNo];
    }, 2500);
  } else if (!debounce && quantity !== itemQuantityPieces) {
    setQuantity(itemQuantityPieces);
  }

  const updateArticle = useUpdateArticle(article.id);

  const handleQuantityChange = async (value: number) => {
    logEvent("ce:MM_articles_quantity_changed");
    if (article?.nbrArticles === value) return;
    try {
      article.nbrArticles = value;
      await updateArticle.mutateAsync({
        ...article, //Roomsettings-service expects a full article object when updating
        nbrArticles: value,
      });
    } catch {
      alert(t("searchErrorString"));
    }
  };

  return (
    <FixaQuantityStepper
      small={true}
      ariaDescribedBy="articleQuantityStepper"
      ariaDescribedById="articleQuantityStepper"
      minVal={1}
      maxVal={99}
      value={quantity}
      onChange={async (event) => {
        const newQuantity = parseInt(event.target.value);
        setDebounce(true);
        setQuantity(newQuantity);
        await handleQuantityChange(newQuantity);
      }}
      onIncrease={async () => {
        const newQuantity = quantity + 1;
        setDebounce(true);
        setQuantity(newQuantity);
        await handleQuantityChange(newQuantity);
      }}
      onDecrease={async () => {
        const newQuantity = quantity - 1;
        setDebounce(true);
        setQuantity(newQuantity);
        await handleQuantityChange(newQuantity);
      }}
    />
  );
};
